import { fg } from '@confluence/feature-gating';

import { ReactionsCommentsQuery } from '../graphql/ReactionsCommentsQuery.graphql';
import {
	ReactionsContentQuery,
	ConfluenceReactionsContentQuery,
} from '../graphql/ReactionsContentQuery.graphql';
import type { ReactionsContentQuery as ReactionsContentQueryData } from '../graphql/__types__/ReactionsContentQuery';
import type { ConfluenceReactionsContentQuery as ConfluenceReactionsContentQueryData } from '../graphql/__types__/ConfluenceReactionsContentQuery';
import type { ReactionsCommentsQuery as ReactionsCommentsQueryData } from '../graphql/__types__/ReactionsCommentsQuery';
import type { QueryData, ReactionsData, GQLContentType } from '../types';

type getReactionsSummaryFn = (data?: QueryData | null) => ReactionsData | null | undefined;

export const getReactionsSummary: getReactionsSummaryFn = (data) => {
	if (!data) {
		return data;
	}

	if (isConfluenceContentReactions(data)) {
		return data?.confluence_contentReactionsSummary?.contentReactionsSummary?.[0];
	}

	if (isContentReactions(data)) {
		return (data as ReactionsContentQueryData)?.content?.nodes?.[0]?.contentReactionsSummary;
	}

	if (isCommentReactions(data)) {
		return (data as ReactionsCommentsQueryData)?.comments?.nodes?.[0]?.reactionsSummary;
	}
};

const isConfluenceContentReactions = (
	data: QueryData,
): data is ConfluenceReactionsContentQueryData =>
	Boolean((data as ConfluenceReactionsContentQueryData).confluence_contentReactionsSummary);

const isContentReactions = (data: QueryData): data is ReactionsContentQueryData =>
	Boolean((data as ReactionsContentQueryData).content);

const isCommentReactions = (data: QueryData): data is ReactionsCommentsQueryData =>
	Boolean((data as ReactionsCommentsQueryData).comments);

const getReactionsContentQuery = () => {
	return fg('use_migrated_content_reactions_summary_endpoint')
		? ConfluenceReactionsContentQuery
		: ReactionsContentQuery;
};

const getReactionsContentQueryVariables = (cloudId, contentType, contentId) => {
	return fg('use_migrated_content_reactions_summary_endpoint')
		? {
				cloudId,
				contentType: contentType?.toUpperCase() as GQLContentType,
				contentId,
			}
		: { contentId };
};

export const getReactionsQueryInfo = (isComment, contentId, containerId, cloudId, contentType) => {
	const query = isComment ? ReactionsCommentsQuery : getReactionsContentQuery();
	const queryVariables = isComment
		? { contentId, containerId }
		: getReactionsContentQueryVariables(cloudId, contentType, contentId);

	return { query, queryVariables };
};
